import { useState, useReducer, useRef } from 'react';

import { fetchPostRepaymentRegisterRepaymentMethod } from '@iwoca/lapi-client/edge';
import { Button, Label } from '@iwoca/orion';
import { Frames, CardNumber, ExpiryDate, Cvv, CardType } from 'frames-react';
import { FormProvider, Resolver, useForm } from 'react-hook-form';

import { Auth3DSecureModal } from './Auth3DSecureModal/Auth3DSecureModal';
import { CheckoutFormValues } from './CheckoutAddPaymentMethodForm.types';
import { pollCardLinking } from './pollCardLinking';
import { validateCheckoutAddCardForm } from './validateFormFields';
import { useGetFundingRequirement } from '../../../../../api/lending/lapiHooks';
import { HookFormInput } from '../../../../../components/Input/Input';
import { InputError } from '../../../../../components/InputError/InputError';
import { useStateKey } from '../../../../../hooks/useStateKey.hook';
import { getRuntimeEnvironment } from '../../../../../utils/getRuntimeEnvironment';
import logger from '../../../../../utils/logger';
import { WhiteLabelOverride } from '../../../../components/WhiteLabelOverride/WhiteLabelOverride';
import { useNavigateToNextRequirement } from '../../../hooks/useNavigateToNextRequirement';

const CHECKOUT_COM_PRODUCTION_KEY = 'pk_m6yjih7mvailyyn4fmgunatoiao';
const CHECKOUT_COM_STAGING_KEY = 'pk_sbox_y67mbbuym66rphki3ktkvbjugmt';

const checkoutResolver: Resolver<CheckoutFormValues> = (values) => {
  const errors = validateCheckoutAddCardForm(values);
  const hasErrors = Object.keys(errors).length > 0;
  return {
    values: hasErrors ? {} : values,
    errors: Object.fromEntries(
      Object.entries(errors).map(([key, message]) => [
        key,
        { type: 'validate', message },
      ]),
    ),
  };
};

export const CheckoutComAddPaymentMethodForm = () => {
  const publicKey =
    getRuntimeEnvironment() === 'PRODUCTION'
      ? CHECKOUT_COM_PRODUCTION_KEY
      : CHECKOUT_COM_STAGING_KEY;

  const { goToNextRequirement } = useNavigateToNextRequirement();
  const forceUpdate = useReducer((x) => x + 1, 0)[1];
  const { fetchFundingRequirements } = useGetFundingRequirement({
    queryOptions: {
      enabled: false,
    },
  });
  const { stateKey } = useStateKey();
  const [submitError, setSubmitError] = useState<string | null>();
  const [postcode, setPostcode] = useState('');
  const [cardholderName, setCardholderName] = useState('');
  const [auth3DSRedirectUrl, setAuth3DSRedirectUrl] = useState<string | null>();
  const [cardTypeError, setCardTypeError] = useState(false);
  const confirmAuth3DSecureCompleteRef = useRef<() => void>(null);

  const methods = useForm<CheckoutFormValues>({
    mode: 'onChange',
    defaultValues: {
      billingPostcode: '',
      cardholderName: '',
      frames: '',
    },
    resolver: checkoutResolver,
  });

  const handleAuth3DSecureRequired = (url: string): Promise<void> => {
    return new Promise((resolve) => {
      setAuth3DSRedirectUrl(url);
      confirmAuth3DSecureCompleteRef.current = resolve;
    });
  };

  const handleAuth3DSecureComplete = () => {
    confirmAuth3DSecureCompleteRef.current!();
    setAuth3DSRedirectUrl(null);
  };

  const handleSubmitAddCheckoutCard = async () => {
    setCardTypeError(false);

    if (!stateKey) {
      throw new Error('No statekey provided');
    }

    const { token, card_type } = await Frames.submitCard();

    try {
      const response = await fetchPostRepaymentRegisterRepaymentMethod({
        stateKey: stateKey,
        body: {
          data: {
            token,
            card_type: card_type as Uppercase<CardType>,
          },
        },
      });

      const redirect_url = response.data.checkout_3ds_redirect_link;
      if (redirect_url) {
        await handleAuth3DSecureRequired(redirect_url);
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      setCardTypeError(true);
      Frames.init();
    }

    const cardLinked = await pollCardLinking(stateKey);
    if (!cardLinked) {
      logger.error('Cant find primary payment card');
      throw new Error(
        'An error occurred while processing your payment method. Please try again.',
      );
    }
  };

  const handleFormSubmit = async () => {
    setSubmitError(null);

    try {
      await handleSubmitAddCheckoutCard();

      await fetchFundingRequirements();
      forceUpdate();

      goToNextRequirement();
    } catch (error: unknown) {
      const err = error as Error;
      setSubmitError(err.message);
      Frames.init();
      methods.reset();
    }
  };

  const {
    errors,
    touchedFields,
    isValid,
    isValidating,
    isDirty,
    isSubmitting,
  } = methods.formState;

  return (
    <FormProvider {...methods}>
      <form
        // eslint-disable-next-line react-hooks/refs -- handleSubmit only invokes the callback at submit time, not during render
        onSubmit={methods.handleSubmit(handleFormSubmit)}
      >
        {cardTypeError && (
          <p className="text-error">
            Credit cards are not supported for automatic repayments. Please use
            a debit card.
          </p>
        )}

        <Frames
          config={{
            publicKey: publicKey,
            cardholder: {
              name: cardholderName,
              billingAddress: { zip: postcode },
            },
            localization: {
              cardNumberPlaceholder: '1234 1234 1234 1234',
              expiryMonthPlaceholder: 'MM',
              expiryYearPlaceholder: 'YY',
              cvvPlaceholder: 'CVC',
            },
            style: {
              base: {
                height: '43px',
                borderRadius: '10px',
                border: '1px solid rgba(204, 204, 204, 1)',
                fontWeight: '400',
                fontFamily: 'Aesop, sans-serif',
                fontSize: '14px',
                padding: '0.5rem',
                width: 'calc(100% - 2px)',
              },
              focus: {
                borderColor: 'rgb(105, 159, 227)',
                borderWidth: '3px',
              },
              invalid: {
                color: 'rgb(175 13 4)',
              },
              placeholder: {
                base: {
                  color: '#8e8e8e',
                },
              },
            },
          }}
        >
          <div className="grid h-[100px] grid-cols-5 gap-l">
            <div className="fs-mask col-span-3">
              <Label className="mb-m">Card number</Label>
              <CardNumber />
            </div>

            <div className="fs-mask">
              <Label className="mb-m">Expiry</Label>
              <ExpiryDate />
            </div>

            <div className="fs-mask">
              <Label className="mb-m">CVC</Label>
              <Cvv />
            </div>
          </div>
        </Frames>

        <div className="mb-2xl grid grid-cols-2 gap-l">
          <div>
            <HookFormInput
              className="fs-mask"
              label="Billing postcode"
              name="billingPostcode"
              placeholder="e.g. N6 6NG"
              showError={false}
              onChange={(e) => {
                setPostcode(e.target.value);
              }}
              type="text"
            />
            <InputError
              isVisible={touchedFields.billingPostcode || false}
              error={errors.billingPostcode?.message}
            />
          </div>
          <div>
            <HookFormInput
              className="fs-mask"
              label="Cardholder name"
              name="cardholderName"
              placeholder="John Smith"
              showError={false}
              type="text"
              onChange={(e) => {
                setCardholderName(e.target.value);
              }}
            />
            <InputError
              isVisible={touchedFields.cardholderName || false}
              error={errors.cardholderName?.message}
            />
          </div>
        </div>

        <WhiteLabelOverride
          componentType="button"
          disabled={isValidating || !(isValid && isDirty)}
        >
          <Button
            disabled={isValidating || !(isValid && isDirty)}
            loading={isSubmitting}
          >
            Add debit card now
          </Button>
        </WhiteLabelOverride>

        <InputError
          isVisible={Boolean(errors.frames)}
          error={errors.frames?.message}
        />
        <InputError
          isVisible={submitError !== undefined}
          error={submitError || ''}
        />

        {auth3DSRedirectUrl && (
          <Auth3DSecureModal
            auth3DSRedirectUrl={auth3DSRedirectUrl}
            onAuth3DSecureComplete={handleAuth3DSecureComplete}
          />
        )}
      </form>
    </FormProvider>
  );
};
